import { useCallback, useEffect, useMemo, useState } from 'react'
import { User } from '../../types'
import { hasPermission } from '../../lib/permissions'
import { auditService } from '../../services/auditService'
import {
  loadInvoiceLedger,
  recordPayment,
  issueInvoice,
  sendInvoiceLink,
  LedgerInvoice,
  INVOICE_STATE_LABEL,
} from '../../services/invoiceLedgerService'
import { transitionDealStatus, DealStatus } from '../../lib/dealStatusService'
import './InvoicesPanel.css'

const money = (n: number) =>
  `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const auDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

const todayIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

type Filter = 'open' | 'draft' | 'paid' | 'all'

export default function InvoicesPanel({ user, onChanged }: { user: User; onChanged?: () => void }) {
  const canManage = hasPermission(user, 'perm-manage-collections')
  const canIssue = hasPermission(user, 'perm-issue-ar')

  const [rows, setRows] = useState<LedgerInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null)
  const [filter, setFilter] = useState<Filter>('open')
  const [busyId, setBusyId] = useState<string | null>(null)

  const [payFor, setPayFor] = useState<LedgerInvoice | null>(null)
  const [payForm, setPayForm] = useState({ amount: '', receivedOn: todayIso(), method: 'eft', reference: '', notes: '' })
  const [payError, setPayError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await loadInvoiceLedger())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load invoices')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const filtered = useMemo(() => {
    switch (filter) {
      case 'draft':
        return rows.filter((r) => r.status === 'draft')
      case 'paid':
        return rows.filter((r) => r.status === 'paid')
      case 'all':
        return rows
      default:
        return rows.filter((r) => r.status !== 'paid' && r.status !== 'cancelled')
    }
  }, [rows, filter])

  const totals = useMemo(
    () => ({
      outstanding: rows.filter((r) => r.status !== 'paid' && r.status !== 'cancelled').reduce((a, r) => a + r.outstanding, 0),
      drafts: rows.filter((r) => r.status === 'draft').length,
      blocked: rows.filter((r) => r.blockedNoEmail).length,
    }),
    [rows]
  )

  const openPay = (row: LedgerInvoice) => {
    setPayFor(row)
    setPayError(null)
    setPayForm({ amount: String(row.outstanding.toFixed(2)), receivedOn: todayIso(), method: 'eft', reference: '', notes: '' })
  }

  const submitPayment = async () => {
    if (!payFor) return
    const amount = parseFloat(payForm.amount)

    if (!Number.isFinite(amount) || amount <= 0) {
      setPayError('Enter the amount received.')
      return
    }
    if (amount > payFor.outstanding + 0.005) {
      setPayError(
        `That is more than the ${money(payFor.outstanding)} outstanding. Record the amount actually received, or raise a revision if the invoice is wrong.`
      )
      return
    }
    if (new Date(`${payForm.receivedOn}T00:00:00`) > new Date()) {
      setPayError('The received date cannot be in the future.')
      return
    }

    setBusyId(payFor.id)
    setPayError(null)
    try {
      const res = await recordPayment({
        invoiceId: payFor.id,
        amount,
        receivedOn: payForm.receivedOn,
        method: payForm.method,
        reference: payForm.reference,
        notes: payForm.notes,
        userId: user.id,
      })

      await auditService.log(user, 'ar_payment_recorded', {
        details: {
          invoice: payFor.invoiceNumber,
          deal: payFor.dealId,
          amount,
          method: payForm.method,
          reference: payForm.reference || null,
          paid_in_full: res.paidInFull,
        },
      })

      let extra = ''
      // AR Paid is what makes the spotter fee payable (Framework H.3), and the
      // deal only reaches Closed once the invoice settles.
      if (res.paidInFull && payFor.dealStatus === 'sold') {
        const moved = await transitionDealStatus(payFor.dealId, 'sold' as DealStatus, 'closed' as DealStatus, user.id, user)
        extra = moved.success ? ' Deal moved to Closed.' : ` Deal not closed: ${moved.message}`
      }

      setNotice({ text: res.message + extra, tone: 'ok' })
      setPayFor(null)
      await load()
      onChanged?.()
    } catch (e) {
      setPayError(e instanceof Error ? e.message : 'Could not record that payment. Nothing was changed.')
    } finally {
      setBusyId(null)
    }
  }

  const handleSmsLink = async (row: LedgerInvoice) => {
    setBusyId(row.id)
    setNotice(null)
    try {
      const res = await sendInvoiceLink(row.id)
      setNotice({ text: res.message, tone: res.ok ? 'ok' : 'error' })
      if (res.ok) {
        await auditService.log(user, 'ar_invoice_link_sms', {
          details: { invoice: row.invoiceNumber, deal: row.dealId },
        })
        await load()
      }
    } catch (e) {
      setNotice({ text: e instanceof Error ? e.message : 'Could not send that SMS', tone: 'error' })
    } finally {
      setBusyId(null)
    }
  }

  const handleIssue = async (row: LedgerInvoice) => {
    setBusyId(row.id)
    setNotice(null)
    try {
      const res = await issueInvoice(row.id)
      setNotice({ text: res.message, tone: res.ok ? 'ok' : 'error' })
      if (res.ok) {
        await auditService.log(user, 'ar_invoice_issued', {
          details: { invoice: row.invoiceNumber, deal: row.dealId, to: row.buyerEmail },
        })
        await load()
        onChanged?.()
      }
    } catch (e) {
      setNotice({ text: e instanceof Error ? e.message : 'Could not issue that invoice', tone: 'error' })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="inv-panel">
      <div className="inv-head">
        <div>
          <h3>Invoices</h3>
          <p>
            Issue a draft to the buyer, and record money when it arrives. An invoice only enters the
            reminder ladder once it has been issued.
          </p>
        </div>
        <div className="inv-stats">
          <div className="inv-stat">
            <span className="inv-stat-value">{money(totals.outstanding)}</span>
            <span className="inv-stat-label">Outstanding</span>
          </div>
          <div className="inv-stat">
            <span className="inv-stat-value">{totals.drafts}</span>
            <span className="inv-stat-label">Not yet issued</span>
          </div>
        </div>
      </div>

      {totals.blocked > 0 && (
        <div className="inv-warn">
          {totals.blocked} invoice{totals.blocked === 1 ? '' : 's'} cannot be issued because the deal has
          no buyer email. Open the deal and fill in <strong>Buyer Details</strong> — until then those
          invoices are invisible to every reminder, the SMS cadence and the aged-debtors position.
        </div>
      )}

      {notice && (
        <div className={`inv-notice ${notice.tone}`} onClick={() => setNotice(null)}>
          {notice.text}
        </div>
      )}

      {error && (
        <div className="inv-notice error">
          <span>{error}</span>
          <button className="inv-btn" onClick={load}>Retry</button>
        </div>
      )}

      <div className="inv-filters">
        {(['open', 'draft', 'paid', 'all'] as Filter[]).map((f) => (
          <button key={f} className={`inv-chip ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>
            {f === 'open' ? 'Open' : f === 'draft' ? 'Not issued' : f === 'paid' ? 'Paid' : 'All'}
          </button>
        ))}
        <button className="inv-btn ghost" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <div className="inv-empty">Loading invoices…</div>
      ) : filtered.length === 0 ? (
        <div className="inv-empty">
          {rows.length === 0
            ? 'No invoices have been generated yet. One is created when a deal reaches Paid.'
            : 'No invoices match this filter.'}
        </div>
      ) : (
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Vehicle</th>
                <th>Buyer</th>
                  <th className="num">Discount</th>
                  <th className="num">Net (ex GST)</th>
                  <th className="num">GST</th>
                <th className="num">Total</th>
                <th className="num">Outstanding</th>
                <th>Due</th>
                <th>State</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const overdue = r.status !== 'paid' && r.daysOverdue > 0
                return (
                  <tr key={r.id} className={r.blockedNoEmail ? 'blocked' : ''}>
                    <td className="mono">{r.invoiceNumber}</td>
                    <td>
                      {r.vehicle}
                      {r.vin && <span className="inv-vin">{r.vin}</span>}
                    </td>
                    <td>
                      {r.buyerName || <span className="inv-missing">No buyer</span>}
                      {!r.buyerEmail && <span className="inv-missing"> · no email</span>}
                    </td>
                    <td className="num">{r.discount === null ? <span className="inv-na">&mdash;</span> : money(r.discount)}</td>
                    <td className="num">{r.netPrice === null ? <span className="inv-na">&mdash;</span> : money(r.netPrice)}</td>
                    <td className="num">
                      {r.gstAmount === null ? (
                        <span className="inv-na">&mdash;</span>
                      ) : (
                        <>
                          {money(r.gstAmount)}
                          {r.taxRate !== null && <span className="inv-rate">{r.taxRate}%</span>}
                        </>
                      )}
                    </td>
                    <td className="num">{money(r.totalAmount)}</td>
                    <td className="num">{r.outstanding > 0 ? money(r.outstanding) : '—'}</td>
                    <td className={overdue ? 'overdue' : ''}>
                      {auDate(r.dueDate)}
                      {overdue && <span className="inv-days">{r.daysOverdue}d</span>}
                    </td>
                    <td>
                      <span className={`inv-state s-${r.status}`}>{INVOICE_STATE_LABEL[r.status] || r.status}</span>
                    </td>
                    <td className="right">
                      {r.status === 'draft' && (
                        <button
                          className="inv-btn"
                          disabled={!canIssue || r.blockedNoEmail || busyId === r.id}
                          title={
                            !canIssue
                              ? 'Requires the perm-issue-ar permission'
                              : r.blockedNoEmail
                              ? 'Add a buyer email on the deal first'
                              : 'Email this invoice to the buyer'
                          }
                          onClick={() => handleIssue(r)}
                        >
                          {busyId === r.id ? 'Sending…' : 'Issue to buyer'}
                        </button>
                      )}
                      {r.status !== 'paid' && r.status !== 'draft' && (
                        <button
                          className="inv-btn"
                          disabled={!canManage || busyId === r.id}
                          title={
                            r.buyerMobile
                              ? 'Text the buyer a link to this invoice'
                              : 'No buyer mobile on file — add it under Buyer Details on the deal'
                          }
                          onClick={() => handleSmsLink(r)}
                        >
                          {r.linkSmsSentAt ? 'Re-send SMS' : 'SMS link'}
                        </button>
                      )}
                      {r.status !== 'paid' && r.status !== 'draft' && (
                        <button
                          className="inv-btn primary"
                          disabled={!canManage || busyId === r.id}
                          title={canManage ? 'Record money received' : 'Requires the perm-manage-collections permission'}
                          onClick={() => openPay(r)}
                        >
                          Record payment
                        </button>
                      )}
                      {r.status === 'paid' && (
                        <span className="inv-paid-on">Paid {auDate(r.paymentReceivedAt)}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {payFor && (
        <div className="inv-modal-overlay" onClick={() => setPayFor(null)} role="dialog">
          <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Record payment</h4>
            <p className="inv-modal-sub">
              {payFor.invoiceNumber} · {payFor.buyerName || 'no buyer'} · {money(payFor.outstanding)} outstanding
            </p>

            <div className="inv-modal-grid">
              <label>
                <span>Amount received</span>
                <input
                  type="number"
                  step="0.01"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                />
              </label>
              <label>
                <span>Date received</span>
                <input
                  type="date"
                  value={payForm.receivedOn}
                  onChange={(e) => setPayForm({ ...payForm, receivedOn: e.target.value })}
                />
              </label>
              <label>
                <span>Method</span>
                <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                  <option value="eft">EFT / bank transfer</option>
                  <option value="card">Card</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                <span>Bank reference</span>
                <input
                  value={payForm.reference}
                  placeholder="Remittance or transaction ref"
                  onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                />
              </label>
            </div>

            <label className="inv-modal-notes">
              <span>Notes</span>
              <textarea
                rows={2}
                value={payForm.notes}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
              />
            </label>

            {parseFloat(payForm.amount) < payFor.outstanding && (
              <p className="inv-modal-hint">
                This is less than the outstanding balance, so the invoice stays open as a part payment
                and continues through the reminder ladder.
              </p>
            )}

            {payError && <div className="inv-notice error">{payError}</div>}

            <div className="inv-modal-actions">
              <button className="inv-btn" onClick={() => setPayFor(null)} disabled={busyId === payFor.id}>
                Cancel
              </button>
              <button className="inv-btn primary" onClick={submitPayment} disabled={busyId === payFor.id}>
                {busyId === payFor.id ? 'Recording…' : 'Record payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
